import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  finalize,
  interval,
  map,
  of,
  tap,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';

export interface AppNotification {
  notification_id: string;
  type: string;
  title: string;
  body: string;
  meta: Record<string, unknown> | null;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  read_at: string | null;
  created_by: string | null;
  created_on: string;
  updated_on?: string;
}

export interface NotificationsListData {
  rows: AppNotification[];
  count: number;
  page: number;
  limit: number;
}

export interface GetNotificationsOptions {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

const UNREAD_POLL_MS = 30_000;

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly baseUrl = environment.baseUrl;
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private pollSub?: Subscription;
  private logoutSub?: Subscription;
  private unreadFetchInFlight = false;

  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {
    this.logoutSub = this.authService.loggedOut$.subscribe(() => {
      this.stopPolling();
    });
  }

  get unreadCount(): number {
    return this.unreadCountSubject.value;
  }

  getNotifications(
    options: GetNotificationsOptions = {},
  ): Observable<NotificationsListData> {
    const page = options.page ?? 1;
    const limit = options.limit ?? 20;
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    if (options.unreadOnly) {
      params = params.set('unread_only', 'true');
    }

    return this.http
      .get<{ data?: NotificationsListData }>(`${this.baseUrl}/notifications`, {
        params,
      })
      .pipe(
        map((res) => ({
          rows: res?.data?.rows ?? [],
          count: res?.data?.count ?? 0,
          page: res?.data?.page ?? page,
          limit: res?.data?.limit ?? limit,
        })),
      );
  }

  getUnreadCount(): Observable<number> {
    return this.http
      .get<{ data?: { count?: number } }>(
        `${this.baseUrl}/notifications/unread-count`,
      )
      .pipe(map((res) => Number(res?.data?.count ?? 0) || 0));
  }

  markAsRead(notificationId: string): Observable<AppNotification | null> {
    return this.http
      .patch<{ data?: AppNotification }>(
        `${this.baseUrl}/notifications/${notificationId}/read`,
        {},
      )
      .pipe(
        map((res) => res?.data ?? null),
        tap((notification) => {
          if (notification && this.unreadCount > 0) {
            this.setUnreadCount(Math.max(0, this.unreadCount - 1));
          } else {
            this.refreshUnreadCount();
          }
        }),
      );
  }

  markAllAsRead(): Observable<{ updated_count?: number }> {
    return this.http
      .patch<{ data?: { updated_count?: number } }>(
        `${this.baseUrl}/notifications/read-all`,
        {},
      )
      .pipe(
        map((res) => res?.data ?? {}),
        tap(() => this.setUnreadCount(0)),
      );
  }

  deleteNotification(
    notificationId: string,
  ): Observable<{
    deleted?: boolean;
    notification_id?: string;
    was_unread?: boolean;
  } | null> {
    return this.http
      .delete<{
        data?: {
          deleted?: boolean;
          notification_id?: string;
          was_unread?: boolean;
        };
      }>(`${this.baseUrl}/notifications/${notificationId}`)
      .pipe(
        map((res) => res?.data ?? null),
        tap((result) => {
          if (result?.was_unread && this.unreadCount > 0) {
            this.setUnreadCount(Math.max(0, this.unreadCount - 1));
          } else if (result?.was_unread == null) {
            this.refreshUnreadCount();
          }
        }),
      );
  }

  clearAllNotifications(): Observable<{ deleted_count?: number }> {
    return this.http
      .delete<{ data?: { deleted_count?: number } }>(
        `${this.baseUrl}/notifications/clear-all`,
      )
      .pipe(
        map((res) => res?.data ?? {}),
        tap(() => this.setUnreadCount(0)),
      );
  }

  deleteNotificationsBulk(
    notificationIds: string[],
  ): Observable<{ deleted_count?: number; unread_deleted_count?: number }> {
    return this.http
      .delete<{
        data?: { deleted_count?: number; unread_deleted_count?: number };
      }>(`${this.baseUrl}/notifications/bulk`, {
        body: { notification_ids: notificationIds },
      })
      .pipe(
        map((res) => res?.data ?? {}),
        tap((result) => {
          const unreadRemoved = Number(result?.unread_deleted_count ?? 0) || 0;
          if (unreadRemoved > 0) {
            this.setUnreadCount(Math.max(0, this.unreadCount - unreadRemoved));
          } else {
            this.refreshUnreadCount();
          }
        }),
      );
  }

  /** Start ~30s unread polling while authenticated. Idempotent. */
  ensurePolling(): void {
    if (!this.authService.isAuthenticated()) {
      this.stopPolling();
      return;
    }
    if (this.pollSub && !this.pollSub.closed) {
      return;
    }

    this.refreshUnreadCount();
    this.pollSub = interval(UNREAD_POLL_MS).subscribe(() => {
      if (!this.authService.isAuthenticated()) {
        this.stopPolling();
        return;
      }
      this.refreshUnreadCount();
    });
  }

  stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.setUnreadCount(0);
  }

  refreshUnreadCount(): void {
    if (!this.authService.isAuthenticated() || this.unreadFetchInFlight) {
      return;
    }
    this.unreadFetchInFlight = true;
    this.getUnreadCount()
      .pipe(
        catchError(() => of(this.unreadCount)),
        finalize(() => {
          this.unreadFetchInFlight = false;
        }),
      )
      .subscribe((count) => this.setUnreadCount(count));
  }

  private setUnreadCount(count: number): void {
    const next = Math.max(0, Number(count) || 0);
    if (next !== this.unreadCountSubject.value) {
      this.unreadCountSubject.next(next);
    }
  }
}
